import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { openCandidate, saveViewState, loadViewState, restoreScroll } from '../utils/navigation';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, Sparkles, Loader2, Eye } from 'lucide-react';
import { searchAPI } from '../api';
import { useTaxonomy } from '../contexts/TaxonomyContext';
import { AreaSubareaFilter } from '../components/AreaSubareaFilter';
import { toast } from 'sonner';
import { getStatusColor, getStatusLabel, getSeniorityLabel } from '../utils/helpers';

const SearchPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getIndustryOptions, getIndustryName, getPresentationAreaName, getPresentationSubareaName } = useTaxonomy();
  // Estado previo guardado (al volver desde una ficha no se vuelve a buscar)
  const saved = location.state?.restoreScrollY != null ? loadViewState(location.pathname) : null;
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(saved?.query || '');
  const [filters, setFilters] = useState(saved?.filters || {});
  const [useSemanticSearch, setUseSemanticSearch] = useState(saved?.useSemanticSearch ?? true);
  const [results, setResults] = useState(saved?.results || []);
  const [hasSearched, setHasSearched] = useState(Boolean(saved?.results?.length));

  useEffect(() => {
    saveViewState(location.pathname, { query, filters, useSemanticSearch, results });
  }, [query, filters, useSemanticSearch, results, location.pathname]);

  useEffect(() => {
    if (saved) restoreScroll(location.state?.restoreScrollY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Obtener opciones de taxonomía desde el contexto
  const industries = getIndustryOptions();

  // Leer parámetro 'q' de la URL y ejecutar búsqueda automáticamente
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    if (saved) return undefined;
    if (urlQuery && urlQuery.trim()) {
      setQuery(urlQuery.trim());
      // Ejecutar búsqueda automáticamente después de un breve delay para permitir que el estado se actualice
      const timer = setTimeout(() => {
        executeSearch(urlQuery.trim());
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const executeSearch = async (searchQuery) => {
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await searchAPI.hybrid({
        query: searchQuery || undefined,
        use_semantic: useSemanticSearch,
        ...filters
      });
      
      // El backend devuelve { results: [], total, search_metadata }
      const data = response.data;
      const candidates = data.results || data || [];
      const total = data.total ?? candidates.length;
      
      setResults(candidates);
      
      if (total === 0) {
        toast.info('No se encontraron resultados');
      } else {
        toast.success(`${total} candidatos encontrados`);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Error en la búsqueda');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    // Actualizar URL con el query actual (sin recargar página)
    if (query.trim()) {
      setSearchParams({ q: query.trim() });
    } else {
      setSearchParams({});
    }
    await executeSearch(query);
  };

  return (
    <Layout title="Búsqueda Avanzada" subtitle="Encuentra candidatos con búsqueda híbrida e IA">
      <div className="space-y-6">
        {/* Search Input */}
        <Card>
          <CardHeader>
            <CardTitle>Búsqueda Inteligente</CardTitle>
            <CardDescription>
              Usa lenguaje natural para buscar candidatos. Humaniq IA encontrará perfiles relevantes incluso si no usan las mismas palabras.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Ej: Director de operaciones con experiencia en manufactura automotriz"
                data-testid="hybrid-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-11 text-base h-12"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="semantic-search"
                checked={useSemanticSearch}
                onCheckedChange={setUseSemanticSearch}
              />
              <Label htmlFor="semantic-search" className="flex items-center gap-2 cursor-pointer">
                <Sparkles className="w-4 h-4 text-cyan-500" />
                <span>Búsqueda semántica con IA</span>
                {useSemanticSearch && (
                  <Badge variant="secondary" className="text-xs">Activado</Badge>
                )}
              </Label>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pt-4 border-t">
              <div>
                <Label>Industria</Label>
                <Select value={filters.industry || ''} onValueChange={(value) => setFilters({ ...filters, industry: value || undefined })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Todas</SelectItem>
                    {industries.map((ind) => (
                      <SelectItem key={ind.value} value={ind.value}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Área Funcional</Label>
                <AreaSubareaFilter
                  area={filters.presentation_area || ''}
                  subarea={filters.presentation_subarea || ''}
                  onChange={(area, subarea) => setFilters({
                    ...filters,
                    presentation_area: area || undefined,
                    presentation_subarea: subarea || undefined
                  })}
                  idPrefix="search-filter"
                  renderOnly="area"
                />
              </div>

              <div>
                <Label>Subárea</Label>
                <AreaSubareaFilter
                  area={filters.presentation_area || ''}
                  subarea={filters.presentation_subarea || ''}
                  onChange={(area, subarea) => setFilters({
                    ...filters,
                    presentation_area: area || undefined,
                    presentation_subarea: subarea || undefined
                  })}
                  idPrefix="search-filter"
                  renderOnly="subarea"
                />
              </div>

              <div>
                <Label>Seniority</Label>
                <Select value={filters.seniority || ''} onValueChange={(value) => setFilters({ ...filters, seniority: value || undefined })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">Todos</SelectItem>
                    <SelectItem value="senior">Senior</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="vp">VP</SelectItem>
                    <SelectItem value="c_level">C-Level</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button
                  onClick={handleSearch}
                  disabled={loading}
                  className="w-full"
                  data-testid="search-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando...</>
                  ) : (
                    <><Search className="w-4 h-4 mr-2" /> Buscar</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Resultados ({results.length})</CardTitle>
              <CardDescription>
                Los resultados están ordenados por relevancia
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {results.map((candidate) => (
                  <div
                    key={candidate.id}
                    className="p-4 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-slate-900">
                            {candidate.full_name}
                          </h3>
                          {candidate.match_score && (
                            <Badge variant="secondary" className="bg-cyan-100 text-cyan-800">
                              Match: {candidate.match_score}%
                            </Badge>
                          )}
                          <Badge className={getStatusColor(candidate.status)}>
                            {getStatusLabel(candidate.status)}
                          </Badge>
                        </div>

                        <p className="text-sm text-slate-600 mb-2">
                          {candidate.current_title && <span className="font-medium">{candidate.current_title}</span>}
                          {candidate.current_company && <span> @ {candidate.current_company}</span>}
                        </p>

                        {candidate.ai_summary && (
                          <p className="text-sm text-slate-700 mb-3 line-clamp-2">
                            {candidate.ai_summary}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2">
                          {candidate.industry && (
                            <Badge variant="outline">{getIndustryName(candidate.industry)}</Badge>
                          )}
                          {candidate.presentation_area && (
                            <Badge variant="outline">
                              {getPresentationAreaName(candidate.presentation_area)}
                              {candidate.presentation_subarea && ` · ${getPresentationSubareaName(candidate.presentation_area, candidate.presentation_subarea)}`}
                            </Badge>
                          )}
                          {candidate.seniority && (
                            <Badge variant="outline">{getSeniorityLabel(candidate.seniority)}</Badge>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCandidate(navigate, location, candidate.id, 'búsqueda')}
                        data-testid={`search-view-profile-${candidate.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Perfil
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default SearchPage;
